import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Callout from 'src/components/InformationCallout/Callout';
import { JuniperEventType } from '../../types';
import LinkPreviewCard from 'src/components/LinkPreviewCard/LinkPreviewCard';
import calcomCreateEventTypePreview from '../../assets/calcom-create-event-type-preview.png';
import {
  IUseUserAssociatedProperties,
  useUserAssociatedProperties,
} from '../../CustomHooks/useUserAssociatedProperties';
import {
  IUseCalcomEventTypes,
  useCalcomEventTypes,
} from '../../CustomHooks/useCalcomEventTypes';
import {
  IUseMyPropertyBookingLinks,
  useMyPropertyBookingLinks,
} from '../../CustomHooks/useMyPropertyBookingLinks/useMyPropertyBookingLinks';
import BookingLinksBody from './BookingLinksBody';
import './booking-links-manager.scss';

const BookingLinksManager = () => {
  // Deep-link target from the /cal-com/manage-bookings/:propertyId(?kind=) subroute — when present,
  // the matching property's card for that kind opens its Manage dialog on mount (e.g. arriving from
  // a "create a link" CTA). Defaults to Walkthrough when no kind is given.
  const { propertyId: deepLinkPropertyId } = useParams<{
    propertyId?: string;
  }>();

  const location = useLocation();

  const deepLinkKindParam: string | null = new URLSearchParams(
    location.search
  ).get('kind');

  const parsedDeepLinkKind: JuniperEventType = Number(
    deepLinkKindParam
  ) as JuniperEventType;

  const deepLinkKind: JuniperEventType = deepLinkKindParam
    ? parsedDeepLinkKind
    : JuniperEventType.Walkthrough;

  const {
    properties,
    isLoading: isLoadingProperties,
    error: propertiesError,
  }: IUseUserAssociatedProperties = useUserAssociatedProperties();

  const {
    eventTypes,
    hasNoEventTypes,
    isLoading: isLoadingEventTypes,
    error: eventTypesError,
  }: IUseCalcomEventTypes = useCalcomEventTypes();

  const {
    linksFor,
    isLoading: isLoadingLinks,
    error: linksError,
    isSubmitting,
    createLink,
    updateLink,
    removeLink,
  }: IUseMyPropertyBookingLinks = useMyPropertyBookingLinks();

  const isLoading: boolean =
    isLoadingProperties || isLoadingEventTypes || isLoadingLinks;

  const error: string | null = propertiesError ?? eventTypesError ?? linksError;

  const hasManageableSections: boolean =
    !isLoading && error === null && !hasNoEventTypes && properties.length > 0;

  return (
    <div className="booking-links-manager">
      <h3 className="booking-links-heading">
        Manage your booking links across properties
      </h3>

      <p className="booking-links-subheading">
        A booking link is a named scheduling link for a property, backed by one
        of your Cal.com event types. Juniper attaches it to walkthrough and
        key-pickup invites so recipients can pick a time from your live
        availability — set one up per property for each kind you offer.
      </p>

      <Callout
        variant="info"
        title="Bookings sync to your calendar"
        subtitle="Walkthrough and key-pickup appointments booked through Juniper are added to your connected Cal.com calendars automatically."
      >
        <p className="booking-links-ownership-note">
          <strong>Booking links are personal for now.</strong> A link can only
          be used by the property developer or admin who created it — you
          can&apos;t share links with other users yet. Shared booking links are
          a feature we&apos;re looking into after beta.
        </p>
      </Callout>

      {hasManageableSections && (
        <Callout
          variant="error"
          title="
          Take care editing event types in Cal.com!"
          subtitle={
            <>
              Your booking links in Juniper depend on their Cal.com event types — handle
              those event types with care.
            </>
          }
        >
          <ul className="booking-links-warning-points">
            <li>
              <strong>Avoid deleting an event type in Cal.com</strong> while
              booking links rely on it. Bookings already made stay on the
              calendar, but the link stops taking new walkthroughs and key
              pickups, and anyone who opens it sees a broken scheduling page.
            </li>
          </ul>

          <LinkPreviewCard
            href="https://cal.com/help/event-types/create-first-event"
            title="Managing event types"
            description="How event types work in Cal.com — the availability behind each booking link."
            siteName="Cal.com Help"
            image={calcomCreateEventTypePreview}
          />
        </Callout>
      )}
      <BookingLinksBody
        isLoading={isLoading}
        error={error}
        hasNoEventTypes={hasNoEventTypes}
        properties={properties}
        eventTypes={eventTypes}
        linksFor={linksFor}
        isSubmitting={isSubmitting}
        onCreate={createLink}
        onUpdate={updateLink}
        onRemove={removeLink}
        deepLinkPropertyId={deepLinkPropertyId}
        deepLinkKind={deepLinkKind}
      />
    </div>
  );
};

export default BookingLinksManager;